import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BoxShell, Panel, Spinner, Stack, Typography } from '@src/components';
import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { AdminPayments } from './AdminPayments';
import { AdminHelpDesk } from './AdminHelpDesk';
import { AdminGoods } from './AdminGoods';
import { AdminSantaGifts } from './AdminSantaGifts';
import { AdminDomains } from './AdminDomains';
import './AdminPage.css';
import { useAppSelector } from '@src/state/hooks';

const tabs = ['premium', 'domains', 'support', 'goods', 'gifts'] as const;
type AdminTab = (typeof tabs)[number];

type Access = { userId: number; status: 'allowed' | 'denied' | 'failed' };

export function AdminPage() {
  const { t, i18n } = useTranslation();
  const { user, status } = useAppSelector((state) => state.session);
  const location = useLocation();
  const navigate = useNavigate();
  const isSuperAdmin = user?.accountRole === 'super_admin';
  const availableTabs: readonly AdminTab[] = isSuperAdmin ? tabs : ['goods'];
  const tab: AdminTab = !isSuperAdmin
    ? 'goods'
    : location.pathname === '/admin/goods'
      ? 'goods'
      : tabs.find((value) => location.hash === `#${value}`) || 'premium';
  function setTab(value: AdminTab) {
    void navigate({
      pathname: value === 'goods' ? '/admin/goods' : '/admin',
      search: location.search,
      hash: value === 'goods' ? '' : `#${value}`,
    });
  }
  const [access, setAccess] = useState<Access | null>(null);
  const userId = user?.id;
  const eligible = Boolean(
    user &&
    !user.isGuest &&
    (user.accountRole === 'super_admin' || /^goods_admin_.+/.test(user.accountRole || '')),
  );
  useEffect(() => {
    if (!eligible || userId === undefined) return;
    const controller = new AbortController();
    void fetch(isSuperAdmin ? '/api/admin' : '/api/admin/goods-access', {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then(async (response) => {
        const granted = response.ok && (await response.json()).authorized === true;
        if (!controller.signal.aborted)
          setAccess({
            userId,
            status: granted
              ? 'allowed'
              : response.ok || [401, 403].includes(response.status)
                ? 'denied'
                : 'failed',
          });
      })
      .catch(() => {
        if (!controller.signal.aborted) setAccess({ userId, status: 'failed' });
      });
    return () => controller.abort();
  }, [eligible, userId, isSuperAdmin]);
  const current = access && access.userId === userId ? access.status : null;
  const loading = status === 'idle' || status === 'loading' || (eligible && current === null);
  const allowed = eligible && current === 'allowed';
  const [goodsPendingCount, setGoodsPendingCount] = useState(0);
  const [domainCount, setDomainCount] = useState(0);
  const [domainRevision, setDomainRevision] = useState(0);
  useEffect(() => {
    if (!allowed || !isSuperAdmin) return;
    const controller = new AbortController();
    let busy = false;
    async function refresh() {
      if (busy) return;
      busy = true;
      try {
        const response = await fetch('/api/admin/domains?filter=pending&page=1', {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        });
        if ([401, 403].includes(response.status)) {
          setAccess({ userId: userId!, status: 'denied' });
          return;
        }
        if (response.ok) {
          const data = await response.json();
          if (!controller.signal.aborted && Number.isSafeInteger(data.total))
            setDomainCount(data.total);
        }
      } catch {
        /* Keep the last known count during transient network errors. */
      } finally {
        busy = false;
      }
    }
    void refresh();
    const timer = window.setInterval(() => void refresh(), 60_000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [allowed, userId, domainRevision, isSuperAdmin]);
  useEffect(() => {
    if (!allowed || !isSuperAdmin) return;
    const controller = new AbortController();
    let busy = false;
    async function refresh() {
      if (busy) return;
      busy = true;
      try {
        const response = await fetch('/api/admin/goods-pending-count', {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        });
        if ([401, 403].includes(response.status)) {
          setAccess({ userId: userId!, status: 'denied' });
          return;
        }
        if (response.ok) {
          const data = await response.json();
          if (!controller.signal.aborted && Number.isSafeInteger(data.total))
            setGoodsPendingCount(data.total);
        }
      } catch {
        /* Keep the last known count during transient network errors. */
      } finally {
        busy = false;
      }
    }
    void refresh();
    const timer = window.setInterval(() => void refresh(), 60_000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [allowed, userId, isSuperAdmin, tab]);
  if (!loading && (!eligible || current === 'denied')) return <Navigate to="/" replace />;
  return (
    <BoxShell width="full" pad="compact" testId={allowed ? 'admin-page' : 'admin-access'}>
      {loading ? (
        <Spinner label={t('account.loading')} />
      ) : (
        <Panel padding="large">
          <Stack gap={4} wide>
            <PageHeading title={t(allowed ? 'admin.title' : 'admin.failedTitle')} />
            {allowed ? (
              <>
                <div role="tablist" aria-label={t('admin.title')} className="admin-tabs">
                  {availableTabs.map((value) => (
                    <button
                      key={value}
                      type="button"
                      role="tab"
                      id={`admin-tab-${value}`}
                      aria-controls={`admin-panel-${value}`}
                      aria-selected={tab === value}
                      tabIndex={tab === value ? 0 : -1}
                      onClick={() => setTab(value)}
                      onKeyDown={(event) => {
                        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                          event.preventDefault();
                          const next =
                            event.key === 'Home'
                              ? availableTabs[0]
                              : event.key === 'End'
                                ? availableTabs[availableTabs.length - 1]
                                : availableTabs[
                                    (availableTabs.indexOf(value) +
                                      (event.key === 'ArrowRight' ? 1 : availableTabs.length - 1)) %
                                      availableTabs.length
                                  ];
                          setTab(next);
                          document.getElementById(`admin-tab-${next}`)?.focus();
                        }
                      }}
                    >
                      {t(`admin.tabs.${value}`)}
                      {value === 'goods' && isSuperAdmin && goodsPendingCount > 0 && (
                        <span
                          className="admin-tabs__badge admin-tabs__badge--goods"
                          aria-label={`${i18n.language.startsWith('ru') ? 'Ожидают одобрения' : 'Pending approval'}: ${goodsPendingCount}`}
                        >
                          {goodsPendingCount}
                        </span>
                      )}
                      {value === 'domains' && domainCount > 0 && (
                        <span
                          className="admin-tabs__badge"
                          aria-label={t('admin.domains.newRequests', { count: domainCount })}
                        >
                          {domainCount}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <div role="tabpanel" id={`admin-panel-${tab}`} aria-labelledby={`admin-tab-${tab}`}>
                  {tab === 'goods' ? (
                    <AdminGoods
                      onPendingCount={setGoodsPendingCount}
                      isSuperAdmin={isSuperAdmin}
                      onDenied={() => setAccess({ userId: userId!, status: 'denied' })}
                    />
                  ) : tab === 'premium' ? (
                    <AdminPayments
                      onDenied={() => setAccess({ userId: userId!, status: 'denied' })}
                    />
                  ) : tab === 'gifts' ? (
                    <AdminSantaGifts
                      onDenied={() => setAccess({ userId: userId!, status: 'denied' })}
                    />
                  ) : tab === 'support' ? (
                    <AdminHelpDesk
                      onDenied={() => setAccess({ userId: userId!, status: 'denied' })}
                    />
                  ) : (
                    <AdminDomains
                      onChanged={() => setDomainRevision((value) => value + 1)}
                      onDenied={() => setAccess({ userId: userId!, status: 'denied' })}
                    />
                  )}
                </div>
              </>
            ) : (
              <Typography as="p" role="body/base">
                {t('admin.failed')}
              </Typography>
            )}
          </Stack>
        </Panel>
      )}
    </BoxShell>
  );
}
